import { Service } from "@lib/titan2-slingo/dist/api/Service";
import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { SlingoLang } from "@lib/titan2-slingo/dist/game/view/abstract/lang/SlingoLang";
import { Clock } from "@lib/titan2/dist/game/control/Clock";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { MessageData } from "@lib/titan2/dist/game/model/data/MessageData";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { CommonLang } from "@lib/titan2/dist/game/view/lang/CommonLang";
import { KeyValuePair } from "@lib/titan2/dist/util/collection/KeyValuePair";
import { EventHandler } from "@lib/titan2/dist/util/event/EventHandler";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { Timer } from "@lib/titan2/dist/util/time/Timer";
import { gsap } from "gsap";
import { FreeSpinReelControllerEvent, GameEvent, TicketViewEvent } from "../../../controller/event/GameEvent";
import { GameLang } from "../lang/GameLang";
import { FeatureTransitionEvents } from "../../../controller/FeatureTransitionController";
import { MessageBar } from "@lib/titan2-slingo/dist/game/view/abstract/display/MessageBar";
import { FeatureGoldFishControllerEvents } from "../../../controller/FeatureGoldFishController";
import { FeatureGreenFishControllerEvents } from "../../../controller/FeatureGreenFishController";
import { FeaturePurpleFishControllerEvents } from "../../../controller/FeaturePurpleFishController";
import { FeatureRedFishControllerEvents } from "../../../controller/FeatureRedFishController";
import { FeatureFishFoodControllerEvents } from "../../../controller/FeatureFishFoodController";
/**
 * GameMessageBar.ts
 */
export class GameMessageBar extends MessageBar {
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    addEventListeners() {
        this.registerGame(CommonGameEvent.PURCHASE_ENTRY, this.handlePurchaseEntry);
        this.registerGame(SlingoEvent.SPIN_RESULT, this.handleSpinResult);
        this.registerGame(SlingoEvent.DAB_ACTIVE, this.handleDabActive);
        this.registerGame(TicketViewEvent.SLINGO_TICKET_ANIMATION, this.handleSlingo);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_TRANSITION_IN_START, this.handleFreeSpinStart);
        this.registerGame(FreeSpinReelControllerEvent.FREE_SPIN_RETRIGGER_START, this.handleFreeSpinStart);
        this.registerGame(GameEvent.COIN_WIN_START, this.handleScatterWinCoin);
        this.registerGame(SlingoEvent.SCATTER_WIN_ANIMATION, this.handleScatterWinJoker);
        this.registerGame(SlingoEvent.AVAILABLE_WIN, this.handleAvailableWin);
        this.registerGame(SlingoEvent.AVAILABLE_WIN_END, this.handleAvailableWinEnd);
        this.registerGame(SlingoEvent.WIN_DISPLAY_START, this.handleComplete);
        this.registerGame(CommonGameEvent.STANDARD_SPIN_RECOVERY, this.handleStandardSpinRecovery);
        this.registerGame(CommonGameEvent.FREE_SPIN_RECOVERY, this.handleFreeSpinRecovery);
        this.registerGame(FeatureTransitionEvents.TRANSITION_TO_GREEN_FISH, this.clearMessages);
        this.registerGame(FeatureTransitionEvents.TRANSITION_TO_RED_FISH, this.clearMessages);
        this.registerGame(FeatureTransitionEvents.TRANSITION_TO_PURPLE_FISH, this.clearMessages);
        this.registerGame(FeatureTransitionEvents.TRANSITION_TO_GOLD_FISH, this.clearMessages);
        this.registerGame(FeatureTransitionEvents.TRANSITION_TO_FISH_FOOD, this.clearMessages);
        this.registerGame(FeatureGreenFishControllerEvents.BEGIN_FEATURE, () => this.setCycle(this.bonusGreenFishKeys, this.cycleIdleMessage));
        this.registerGame(FeatureRedFishControllerEvents.BEGIN_FEATURE, () => this.setCycle(this.bonusRedFishKeys, this.cycleIdleMessage));
        this.registerGame(FeaturePurpleFishControllerEvents.BEGIN_FEATURE, () => this.setCycle(this.bonusPurpleFishKeys, this.cycleIdleMessage));
        this.registerGame(FeatureGoldFishControllerEvents.BEGIN_FEATURE, () => this.setCycle(this.bonusGoldFishKeys, this.cycleIdleMessage));
        this.registerGame(FeatureFishFoodControllerEvents.BEGIN_FEATURE, () => this.setCycle(this.bonusFishFoodKeys, this.cycleIdleMessage));
        this.registerGame(CommonGameEvent.STAKE_UPDATED, this.updateGameId);
        this.registerCore(CommonGameEvent.BALANCE_UPDATED, this.updateGameId);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN, this.updateGameId);
        this.registerGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY, this.updateGameId);
        this.registerGame(SlingoEvent.BALANCE_UPDATE, this.updateGameId);
        if (this.responsive) {
            this.registerCore(ResizeEvent.RESIZE, this.handleResize);
            this.registerCore(ResizeEvent.ROTATE, this.handleRotate);
        }
        this.registerCore(ResizeEvent.TRACK_TARGETS, this.handleTrackTargets);
    }
    updateGameId() {
        if (this.gameId) {
            let gameId = this.model.getGameInstanceId();
            if (gameId > 0) {
                let params = [new KeyValuePair(CommonLang.PARAM_AMOUNT, gameId)];
                let message = this.langManager.getLang(GameLang.BOTTOM_BAR_GAME_ID, params);
                this.gameId.setText(message, true);
            }
            else {
                this.gameId.clear();
            }
            this.positionMessageField();
        }
    }
    createBackground() {
        this.background = new TitanSprite(this.landscapeProperties.backgroundProperties, this.portraitProperties?.backgroundProperties);
        this.background.x = this.renderer.getViewport().width / 2;
        this.addChild(this.background);
    }
    createClock() {
        this.clock = new Clock(this.landscapeProperties.clockProperties, this.portraitProperties?.clockProperties);
        this.addChild(this.clock);
    }
    createGameIdField() {
        if (this.uiProperties.gameIdProperties) {
            this.gameId = TitanText.createField(this, this.landscapeProperties.gameIdProperties, this.portraitProperties?.gameIdProperties);
            this.addChild(this.gameId);
        }
    }
    handleRotate(event) {
        super.handleRotate(event);
        this.uiProperties = this.uiProperties;
    }
    // message bar:   
    createMessageField() {
        this.messageField = TitanText.createField(this, this.landscapeProperties.messageFieldProperties, this.portraitProperties?.messageFieldProperties);
        this.addChild(this.messageField);
    }
    createMessages() {
        this.idleMessageKeys = this.createMessageKeys(GameLang.IDLE, this.data.idleMessages);
        this.idleStandardSpinMessageKeys = this.createMessageKeys(GameLang.IDLE_STANDARD_SPIN, this.data.idleStandardSpinMessages);
        this.idleFreeSpinMessageKeys = this.createMessageKeys(GameLang.IDLE_FREE_SPIN, this.data.idleFreeSpinMessages);
        this.chooseCellMessageKeys = this.createMessageKeys(GameLang.CHOOSE_CELL, this.data.chooseCellMessages);
        this.chooseCellJokerMessageKeys = this.createMessageKeys(GameLang.CHOOSE_CELL_JOKER, this.data.chooseCellJokerMessages);
        this.chooseCellSuperJokerMessageKeys = this.createMessageKeys(GameLang.CHOOSE_CELL_SUPER_JOKER, this.data.chooseCellSuperJokerMessages);
        this.bonusGreenFishKeys = this.createBonusMessageKeys(GameLang.BONUS_GREEN_LINE, 2);
        this.bonusRedFishKeys = this.createBonusMessageKeys(GameLang.BONUS_RED_LINE, 2);
        this.bonusPurpleFishKeys = this.createBonusMessageKeys(GameLang.BONUS_PURPLE_LINE, 2);
        this.bonusGoldFishKeys = this.createBonusMessageKeys(GameLang.BONUS_GOLD_LINE, 2);
        this.bonusFishFoodKeys = this.createBonusMessageKeys(GameLang.BONUS_FOOD_LINE, 2);
    }
    createMessageTimer() {
        this.messageTimer = new Timer(this.uiProperties.messageInterval);
        this.messageTimer.onUpdate(this.handleMessageTimerUpdated, this);
    }
    handlePurchaseEntry() {
        this.setCycle(this.idleMessageKeys, this.cycleIdleMessage);
    }
    handleSpinResult() {
        let state = this.model.getPreviousState();
        if (state.isStandardSpin()) {
            this.setSingle(this.singleStandardSpinMessage());
        }
        else if (state.isFreeSpin()) {
            this.setSingle(this.singleMessage(GameLang.FREE_SPIN));
        }
        else if (state.isPurchaseSpin()) {
            this.setSingle(this.singleMessage(GameLang.PURCHASED_SPIN));
        }
    }
    handleFreeSpinStart() {
        this.setSingle((this.singleMessage(GameLang.REEL_STRIKE_FREE_SPIN)));
    }
    handleFreeSpinRetrigger() {
        this.setSingle((this.singleMessage(GameLang.REEL_STRIKE_FREE_SPIN)));
    }
    handleDabActive() {
        let dabData = this.model.getDabData();
        if (dabData.hasSuperJokerCellToDab()) {
            this.setCycle(this.chooseCellSuperJokerMessageKeys, this.cycleMessage);
        }
        else if (dabData.hasJokerCellToDab()) {
            this.setCycle(this.chooseCellJokerMessageKeys, this.cycleMessage);
        }
        else {
            this.setCycle(this.chooseCellMessageKeys, this.cycleMessage);
        }
    }
    handleFreeSpin() {
        this.setCycle(this.idleFreeSpinMessageKeys, this.cycleMessage);
    }
    handleAvailableWin() {
        if (this.operatorConfig.isHitForEnabled()) {
            this.setSingle(this.singleAvailableWinMessage());
        }
        else {
            this.clearMessages();
        }
    }
    handleAvailableWinEnd() {
        this.clearMessages();
    }
    handleScatterWinJoker() {
        this.setSingle(this.singleScatterWinJokerMessage());
    }
    handleScatterWinCoin() {
        this.setSingle(this.singleScatterWinCoinMessage());
    }
    handleSlingo() {
        this.clearMessages();
    }
    handleComplete() {
        this.clearMessages();
    }
    handleStandardSpinRecovery() {
        this.updateGameId();
        if (this.model.isSpin()) {
            this.setSingle(this.singleMessage(`${GameLang.IDLE_STANDARD_SPIN}1`));
        }
    }
    handleFreeSpinRecovery() {
        this.updateGameId();
        if (this.model.isSpin()) {
            this.setSingle(this.singleMessage(`${GameLang.IDLE_FREE_SPIN}1`));
        }
    }
    handleMessageTimerUpdated() {
        if (this.currentMessage.hasNext()) {
            this.updateMessage(this.currentMessage);
        }
        else {
            if (this.cycling) {
                let index = this.messageCycleIndex + 1;
                this.messageCycleIndex = index < this.messagesToCycle.length ? index : 0;
                this.nextMessage();
            }
            else {
                this.messageTimer.stop();
            }
        }
    }
    setSingle(message) {
        this.messageTimer.stop();
        this.cycling = false;
        this.updateMessage(message);
        this.messageTimer.start();
    }
    setCycle(messages, action, updateMessage = true) {
        this.messageTimer.stop();
        this.cycling = true;
        this.messagesToCycle = messages;
        this.messageCycleIndex = 0;
        this.cycleMessageHandler = new EventHandler(action, this);
        this.messageTimer.start();
        if (updateMessage) {
            this.nextMessage();
        }
    }
    clearMessages() {
        this.messageTimer.stop();
        this.cycling = false;
        this.messagesToCycle = [];
        this.cycleMessageHandler = null;
        this.updateMessage(new MessageData("", 10));
    }
    nextMessage() {
        let key = this.messagesToCycle[this.messageCycleIndex];
        if (this.cycleMessageHandler) {
            this.cycleMessageHandler.call(key);
        }
    }
    updateMessage(message) {
        this.currentMessage = message;
        if (Service.isReplay()) {
            this.messageField.setText(this.langManager.getLang("GameLang.OPTIONS_DGH"), true);
            this.messageField.alpha = 1;
        }
        else {
            this.messageField.alpha = 0;
            this.messageField.setText(this.currentMessage.next(), true);
            gsap.to(this.messageField, { alpha: 1, duration: 0.5 });
        }
        this.positionMessageField();
    }
    cycleMessage(key) {
        this.updateMessage(this.createMessageData(key));
    }
    cycleIdleMessage(key) {
        const gameConfig = this.model.getGameConfig();
        const maxWin = this.formatCurrency(this.model.getMaxWin());
        const scatter5 = this.formatCurrency(this.model.getScatterPrize(5));
        const scatter4 = this.formatCurrency(this.model.getScatterPrize(4));
        const scatter3 = this.formatCurrency(this.model.getScatterPrize(3));
        const gameName = this.model.getGameId() === "slingo-irishxxxtreme" ? "Irishxxxtreme" : null;
        let params = [
            new KeyValuePair(GameLang.PARAM_GAME, gameName),
            new KeyValuePair(SlingoLang.PARAM_SPINS, gameConfig.getNumberOfSpins()),
            new KeyValuePair(CommonLang.PARAM_AMOUNT, maxWin),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_5, scatter5),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_4, scatter4),
            new KeyValuePair(SlingoLang.PARAM_SCATTER_3, scatter3)
        ];
        this.updateMessage(this.createMessageData(key, params));
    }
    cycleIdleStandardSpinMessage(key) {
        let config = this.model.getGameProgress();
        if (config) {
            let params = [
                new KeyValuePair(SlingoLang.PARAM_SPINS, config.getSpinsRemaining())
            ];
            this.updateMessage(this.createMessageData(key, params));
        }
    }
    singleMessage(key) {
        return this.createMessageData(key);
    }
    singleStandardSpinMessage() {
        let gameConfig = this.model.getGameConfig();
        let progress = this.model.getGameProgress();
        let standardSpins = progress.getStandardSpins();
        let numberOfSpins = standardSpins > 0 ? standardSpins : gameConfig.getNumberOfSpins();
        let currentSpin = numberOfSpins - progress.getSpinsRemaining();
        let params = [
            new KeyValuePair(SlingoLang.PARAM_SPIN, currentSpin),
            new KeyValuePair(SlingoLang.PARAM_SPINS, numberOfSpins)
        ];
        return this.createMessageData(SlingoLang.STANDARD_SPIN, params);
    }
    singleScatterWinJokerMessage() {
        const currentStake = this.model.getCurrentStake();
        const symbolPayouts = this.model.getPayoutConfig().getSymbolPayouts().getValue(undefined).getMultipliers();
        const result = this.model.getSpinResult();
        let symbolWinIndex = 0;
        if (result?.hasSymbolWin() && result.getSymbolWins()[0].getNumberOfSymbols() >= 3) {
            symbolWinIndex = result.getSymbolWins()[0].getNumberOfSymbols() - 1;
        }
        const win = currentStake * symbolPayouts[symbolWinIndex];
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win))
        ];
        let key = SlingoLang.SYMBOL_WIN + result.getSymbolWins()[0].getNumberOfSymbols() + SlingoLang.SYMBOL_WIN_JOKER + 0;
        return this.createMessageData(key, params);
    }
    singleScatterWinCoinMessage() {
        let result = this.model.getSpinResult();
        let win = result.getCoinWin();
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(win.getAmount()))
        ];
        let key = SlingoLang.SYMBOL_WIN_COIN + 0;
        return this.createMessageData(key, params);
    }
    singleDevilMessage() {
        return this.createMessageData(GameLang.DEVIL + RandomUtil.nextInt(0, 2));
    }
    singleAvailableWinMessage() {
        const availableWin = this.model.getAvailableWin();
        const numberOfPatterns = availableWin.getNumberOfPatterns();
        const key = numberOfPatterns === 12
            ? GameLang.PURCHASE_SPIN_6
            : GameLang[`PURCHASE_SPIN_${numberOfPatterns - 5}`];
        const params = [new KeyValuePair(GameLang.PARAM_CELLS, availableWin.getKey())];
        return this.createMessageData(key, params);
    }
    singleSlingoCompleteMessage(patternWin) {
        let params = [
            new KeyValuePair(CommonLang.PARAM_AMOUNT, this.formatCurrency(patternWin))
        ];
        return this.createMessageData(GameLang.SLINGO_COMPLETE, params);
    }
    createMessageKeys(key, total) {
        let keys = [];
        for (let i = 0; i < total; i++) {
            keys.push(key + i);
        }
        return keys;
    }
    createBonusMessageKeys(key, total) {
        let keys = [];
        for (let i = 1; i <= total; i++) {
            keys.push(key + i);
        }
        return keys;
    }
    createMessageData(key, params) {
        return new MessageData(this.langManager.getLang(key, params), this.maxLength);
    }
    handleResize(event) {
        const transFormedScale = this.getTransformedScale(this, this.scale);
        const renderView = this.renderer.getView();
        const renderWidth = parseInt(renderView.style.width, 10) || renderView.width;
        const distanceToTop = 0 - this.getGlobalPosition().y;
        const distanceToLeft = 0 - this.gameId.getGlobalPosition().x;
        const distanceToRight = renderWidth - this.clock.getGlobalPosition().x;
        this.y += distanceToTop / transFormedScale.y;
        //Frustratingly, you can't detect fullscreen if the game is in an iframe. The only solution is to force the offset at all times.
        const fullScreenOffset = this.uiProperties.fullScreenOffset; // document.fullscreenElement ? this.uiProperties.fullScreenOffset : 0;
        this.gameId.x += distanceToLeft / transFormedScale.x + this.uiProperties.fieldOffset + fullScreenOffset;
        this.clock.x += distanceToRight / transFormedScale.x - this.uiProperties.fieldOffset - fullScreenOffset;
        this.background.width = renderWidth / transFormedScale.x;
        this.positionMessageField();
    }
    handleReinitialiseText() {
        this.positionMessageField();
    }
    positionMessageField() {
        const messageFieldWidth = this.messageField.width ? this.messageField.width : this.uiProperties.messageFieldProperties.size.x;
        const hasOverlap = this.gameId.x + this.uiProperties.gameIdProperties.size.x > this.uiProperties.messageFieldProperties.position.x - messageFieldWidth / 2;
        if (this.gameId.getText() && hasOverlap) {
            this.messageField.x = this.gameId.x + this.uiProperties.gameIdProperties.size.x + this.uiProperties.messageFieldProperties.size.x / 2;
        }
        else {
            this.messageField.x = this.uiProperties.messageFieldProperties.position.x;
        }
    }
}
//# sourceMappingURL=GameMessageBar.js.map